import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { requireAccess } from '../lib/guards';
import { zeiterfassungApi, type AntragTyp, type EintragTyp } from '../lib/zeiterfassung-api';

type FormTyp = 'urlaub' | 'krank' | 'korrektur' | 'manueller_eintrag';

const TAB_LABEL: Record<FormTyp, string> = {
  urlaub: 'Urlaub',
  krank: 'Krank',
  korrektur: 'Korrektur',
  manueller_eintrag: 'Manueller Eintrag',
};

const EINTRAG_TYP_LABEL: Record<EintragTyp, string> = {
  kommen: 'Kommen',
  gehen: 'Gehen',
  pause_start: 'Pause Start',
  pause_ende: 'Pause Ende',
};

export const Route = createFileRoute('/zeiterfassung/antrag')({
  beforeLoad: () => requireAccess('/api/v1/zeiterfassung/status'),
  component: AntragPage,
});

function AntragPage(): JSX.Element {
  const [tab, setTab] = useState<FormTyp>('urlaub');

  return (
    <div className="space-y-4 max-w-2xl">
      <PageHeader
        title="Antrag stellen"
        description="Urlaub, Krankmeldung, Korrektur oder manueller Stempel."
      />

      <div className="rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <nav className="grid grid-cols-2 gap-1 sm:grid-cols-4">
          {(Object.keys(TAB_LABEL) as FormTyp[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                tab === t ? 'bg-brand-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'urlaub' ? <UrlaubForm /> : null}
      {tab === 'krank' ? <KrankForm /> : null}
      {tab === 'korrektur' ? <EintragForm formTyp="korrektur" /> : null}
      {tab === 'manueller_eintrag' ? <EintragForm formTyp="manueller_eintrag" /> : null}
    </div>
  );
}

// ============================================================================
// Urlaub mit Konflikt-Check
// ============================================================================

function UrlaubForm(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [startDatum, setStart] = useState('');
  const [endDatum, setEnd] = useState('');
  const [notiz, setNotiz] = useState('');

  const datesValid = isDate(startDatum) && isDate(endDatum) && startDatum <= endDatum;

  const konflikteQ = useQuery({
    queryKey: ['zeiterfassung', 'konflikte', startDatum, endDatum],
    queryFn: () => zeiterfassungApi.getUrlaubsKonflikte(startDatum, endDatum),
    enabled: datesValid,
  });

  const submit = useMutation({
    mutationFn: () =>
      zeiterfassungApi.antragErstellen('urlaub', {
        startDatum,
        endDatum,
        ...(notiz ? { notiz } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
      void navigate({ to: '/zeiterfassung' });
    },
  });

  const konflikte = konflikteQ.data?.items ?? [];
  const blockedBySubmit = !datesValid || submit.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Start">
          <Input
            type="date"
            value={startDatum}
            onChange={(e) => setStart(e.target.value)}
            required
          />
        </Field>
        <Field label="Ende">
          <Input type="date" value={endDatum} onChange={(e) => setEnd(e.target.value)} required />
        </Field>
      </div>
      <Field label="Notiz (optional)">
        <Input
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          placeholder="z.B. Familienurlaub"
        />
      </Field>

      {datesValid && konflikte.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          ⚠ {konflikte.length} Kollegen-Urlaub(e) im Zeitraum:
          <ul className="mt-1 list-disc pl-5 text-xs">
            {konflikte.map((k, i) => (
              <li key={`${k.userId}-${i}`}>
                {k.startDatum} – {k.endDatum}
              </li>
            ))}
          </ul>
          Du kannst trotzdem einreichen — Buero entscheidet.
        </div>
      ) : null}

      {submit.isError ? <ErrorBanner error={submit.error as ApiError} /> : null}

      <Button type="submit" disabled={blockedBySubmit}>
        {submit.isPending ? 'Sende …' : 'Urlaub beantragen'}
      </Button>
    </form>
  );
}

// ============================================================================
// Krank — Auto-Approve im Backend
// ============================================================================

function KrankForm(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const heute = new Date().toISOString().slice(0, 10);
  const [startDatum, setStart] = useState(heute);
  const [endDatum, setEnd] = useState(heute);
  const [notiz, setNotiz] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      zeiterfassungApi.antragErstellen('abwesenheit', {
        abwesenheitTyp: 'krank',
        startDatum,
        endDatum,
        ...(notiz ? { notiz } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
      void navigate({ to: '/zeiterfassung' });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
        Krankmeldungen werden automatisch genehmigt; das Büro wird informiert.
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Erster Krankheitstag">
          <Input
            type="date"
            value={startDatum}
            onChange={(e) => setStart(e.target.value)}
            required
          />
        </Field>
        <Field label="Voraussichtl. letzter Tag">
          <Input type="date" value={endDatum} onChange={(e) => setEnd(e.target.value)} required />
        </Field>
      </div>
      <Field label="Notiz (optional)">
        <Input
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          placeholder="z.B. AU bis Freitag"
        />
      </Field>

      {submit.isError ? <ErrorBanner error={submit.error as ApiError} /> : null}

      <Button type="submit" disabled={submit.isPending}>
        {submit.isPending ? 'Sende …' : 'Krankmeldung absenden'}
      </Button>
    </form>
  );
}

// ============================================================================
// Korrektur / Manueller Eintrag — gleicher Form-Aufbau, anderer typ
// ============================================================================

function EintragForm({ formTyp }: { formTyp: 'korrektur' | 'manueller_eintrag' }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const heute = new Date().toISOString().slice(0, 10);
  const [datum, setDatum] = useState(heute);
  const [eTyp, setETyp] = useState<EintragTyp>('kommen');
  const [zeit, setZeit] = useState('08:00');
  const [begruendung, setBegruendung] = useState('');

  const submit = useMutation({
    mutationFn: () => {
      const zeitpunkt = new Date(`${datum}T${zeit}:00`).toISOString();
      const antragTyp: AntragTyp = formTyp;
      return zeiterfassungApi.antragErstellen(antragTyp, {
        datum,
        typ: eTyp,
        zeitpunkt,
        begruendung,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
      void navigate({ to: '/zeiterfassung' });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
        {formTyp === 'korrektur'
          ? 'Korrektur eines bestehenden Stempels (z.B. falscher Zeitpunkt). Geht durchs Büro.'
          : 'Manueller Eintrag (z.B. vergessen zu stempeln). Geht durchs Büro.'}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Datum">
          <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} required />
        </Field>
        <Field label="Stempel-Typ">
          <Select value={eTyp} onChange={(e) => setETyp(e.target.value as EintragTyp)}>
            {(['kommen', 'gehen', 'pause_start', 'pause_ende'] as EintragTyp[]).map((t) => (
              <option key={t} value={t}>
                {EINTRAG_TYP_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Uhrzeit">
          <Input type="time" value={zeit} onChange={(e) => setZeit(e.target.value)} required />
        </Field>
      </div>
      <Field label="Begründung">
        <Input
          value={begruendung}
          onChange={(e) => setBegruendung(e.target.value)}
          required
          placeholder="z.B. App war abgestürzt"
        />
      </Field>

      {submit.isError ? <ErrorBanner error={submit.error as ApiError} /> : null}

      <Button type="submit" disabled={submit.isPending || begruendung.trim().length === 0}>
        {submit.isPending ? 'Sende …' : 'Antrag absenden'}
      </Button>
    </form>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function isDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}
